import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faChevronDown, faChevronRight, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../../core/components/Button.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import FieldPathInput from '../components/FieldPathInput.tsx';
import TargetFieldSelect from '../components/TargetFieldSelect.tsx';
import NestedActionList from '../components/NestedActionList.tsx';
import type {
    MapArrayAction,
    FieldMapping,
    Action,
    AliasContextEntry,
    ActionTypeInfo,
    TargetFieldOption,
} from '../types.ts';

const PlusIcon = withIcon(faPlus);
const TrashIcon = withIcon(faTrash);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronRightIcon = withIcon(faChevronRight);

interface MapArrayBlockProps {
    action: MapArrayAction;
    mappingIndex: number;
    actionIndex: number;
    aliasContext?: AliasContextEntry[];
}

interface NestedFieldMappingProps {
    mapping: FieldMapping;
    index: number;
    parentMappingIndex: number;
    parentActionIndex: number;
    onUpdate: (updates: Partial<FieldMapping>) => void;
    onDelete: () => void;
    aliasContext: AliasContextEntry[];
    availableActionTypes: ActionTypeInfo[];
    targetOptions: TargetFieldOption[];
}

const NestedFieldMapping = ({
    mapping,
    index,
    parentMappingIndex,
    parentActionIndex,
    onUpdate,
    onDelete,
    aliasContext,
    availableActionTypes,
    targetOptions,
}: NestedFieldMappingProps) => {
    const { t } = useTranslation('integrations');
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="border border-line-weak rounded-lg bg-surface-weak">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-line-weak bg-surface-medium rounded-t-lg">
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-content-weak hover:text-content-strong transition-colors"
                >
                    {isExpanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                </button>
                <span className="flex-1 text-sm font-medium truncate">{mapping.target || 'New Field'}</span>
                <button
                    type="button"
                    onClick={onDelete}
                    className="text-content-weak hover:text-status-danger-medium transition-colors"
                >
                    <TrashIcon className="w-4 h-4" />
                </button>
            </div>
            {isExpanded && (
                <div className="p-3 flex flex-col gap-3">
                    <div>
                        <label className="text-xs text-content-weak mb-1 block">
                            {t('outputMapper.visualEditor.targetField')}
                        </label>
                        {targetOptions.length > 0 ? (
                            <TargetFieldSelect
                                options={targetOptions}
                                value={mapping.target}
                                onChange={(target) => onUpdate({ target })}
                                placeholder={t('outputMapper.visualEditor.selectTargetField')}
                            />
                        ) : (
                            <StringField
                                value={mapping.target}
                                onValueChange={(target) => onUpdate({ target })}
                                placeholder={t('outputMapper.visualEditor.selectTargetField')}
                            />
                        )}
                    </div>
                    <div>
                        <div className="text-xs text-content-weak mb-2">{t('outputMapper.visualEditor.actions')}</div>
                        <NestedActionList
                            actions={mapping.actions}
                            mappingIndex={parentMappingIndex}
                            keyPrefix={`${parentMappingIndex}-${parentActionIndex}-nested-${index}`}
                            aliasContext={aliasContext}
                            availableActionTypes={availableActionTypes}
                            onUpdateActions={(actions) => onUpdate({ actions: actions as Action[] })}
                            filterOutActionTypes={['map_array']}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const MapArrayBlock = ({ action, mappingIndex, actionIndex, aliasContext = [] }: MapArrayBlockProps) => {
    const { t } = useTranslation('integrations');
    const { updateAction, config, flattenFields, availableActionTypes } = useOutputMapperEditor();

    const parentTarget = config.output_mapping[mappingIndex]?.target;
    const lineTargetOptions = useMemo(
        () => (config.target_field_options ?? []).filter((option) => option.array_target === parentTarget),
        [config.target_field_options, parentTarget]
    );
    const usedTargets = useMemo(
        () => new Set(action.field_mappings.map((mapping) => mapping.target)),
        [action.field_mappings]
    );

    const itemTypeName = useMemo((): string | undefined => {
        if (!action.source) return undefined;
        const sourceParts = action.source.split('.');
        if (sourceParts.length < 2) return undefined;

        const inputType = config.input_types.find((t) => t.name === sourceParts[0]);
        if (!inputType) return undefined;

        let currentFields = inputType.fields;
        const fieldPath = sourceParts.slice(1);
        for (let i = 0; i < fieldPath.length; i++) {
            const field = currentFields.find((f) => f.name === fieldPath[i]);
            if (!field) return undefined;
            if (i === fieldPath.length - 1) {
                return field.type === 'array' && field.item_type ? field.item_type : undefined;
            }
            if (field.nested_fields) {
                currentFields = field.nested_fields;
            } else if (field.object_type) {
                const nestedType = config.input_types.find((t) => t.name === field.object_type);
                if (nestedType) currentFields = nestedType.fields;
                else return undefined;
            } else {
                return undefined;
            }
        }
        return undefined;
    }, [action.source, config.input_types]);

    const nestedAliasContext: AliasContextEntry[] = [
        ...aliasContext,
        { alias: action.item_alias || 'item', typeName: itemTypeName },
        ...(action.index_alias ? [{ alias: action.index_alias }] : []),
    ];

    const handleAddFieldMapping = () => {
        const newMapping: FieldMapping = { target: '', actions: [], is_required: false };
        updateAction(mappingIndex, actionIndex, { field_mappings: [...action.field_mappings, newMapping] });
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.sourceArray')}
                </label>
                <FieldPathInput
                    value={action.source}
                    onChange={(source) => updateAction(mappingIndex, actionIndex, { source })}
                    aliasContext={aliasContext}
                    placeholder={t('outputMapper.visualEditor.selectArrayField')}
                />
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-content-weak mb-1 block">
                        {t('outputMapper.visualEditor.itemAlias')}
                    </label>
                    <div className="flex items-center gap-1">
                        <span className="text-content-weak">@</span>
                        <StringField
                            value={action.item_alias || 'item'}
                            onValueChange={(item_alias) => updateAction(mappingIndex, actionIndex, { item_alias })}
                            placeholder="item"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs text-content-weak mb-1 block">
                        {t('outputMapper.visualEditor.indexAlias')}
                    </label>
                    <div className="flex items-center gap-1">
                        <span className="text-content-weak">@</span>
                        <StringField
                            value={action.index_alias || ''}
                            onValueChange={(v) =>
                                updateAction(mappingIndex, actionIndex, { index_alias: v || undefined })
                            }
                            placeholder={t('outputMapper.visualEditor.optional')}
                        />
                    </div>
                </div>
            </div>

            <div className="text-xs text-content-weak bg-surface-medium p-2 rounded">
                {t('outputMapper.visualEditor.mapArrayHelp', {
                    item: `@${action.item_alias || 'item'}`,
                    index: action.index_alias ? `@${action.index_alias}` : '',
                })}
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-content-strong">
                        {t('outputMapper.visualEditor.fieldMappings')}
                    </div>
                    <Button variant="ghost" type="button" className="text-xs" onClick={handleAddFieldMapping}>
                        <PlusIcon className="w-3 h-3 mr-1" />
                        {t('outputMapper.visualEditor.addFieldMapping')}
                    </Button>
                </div>
                <div className="flex flex-col gap-2">
                    {action.field_mappings.map((mapping, index) => (
                        <NestedFieldMapping
                            key={`${mappingIndex}-${actionIndex}-field-${index}`}
                            mapping={mapping}
                            index={index}
                            parentMappingIndex={mappingIndex}
                            parentActionIndex={actionIndex}
                            targetOptions={lineTargetOptions.filter(
                                (option) => option.target === mapping.target || !usedTargets.has(option.target)
                            )}
                            onUpdate={(updates) => {
                                const newMappings = action.field_mappings.map((m, i) =>
                                    i === index ? { ...m, ...updates } : m
                                );
                                updateAction(mappingIndex, actionIndex, { field_mappings: newMappings });
                            }}
                            onDelete={() => {
                                updateAction(mappingIndex, actionIndex, {
                                    field_mappings: action.field_mappings.filter((_, i) => i !== index),
                                });
                            }}
                            aliasContext={nestedAliasContext}
                            availableActionTypes={availableActionTypes}
                        />
                    ))}
                    {action.field_mappings.length === 0 && (
                        <div className="text-sm text-content-weak text-center py-4 border border-dashed border-line-weak rounded">
                            {t('outputMapper.visualEditor.noFieldMappings')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MapArrayBlock;
